import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { io, type Socket } from 'socket.io-client';
import type { MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum.ts';
import type { PlayerParentEntity } from '@/types/dtos/player/player-parent-entity';

export type PlayerSocketEntity = MediaParentEntity | PlayerParentEntity;
import {
  applyPerformControlWsPayload,
  entityRoomKeyFromEntityRef,
  type LiveContextPerformControlWsPayloadV1,
} from '@/state/player/perform-control-ws.store';
import {
  applyLivePlayStateWsPayload,
  type LiveContextPlayStateWsPayloadV1,
} from '@/state/player/live-play-state-ws.store';
import type { SequenceIndexChangePayload } from '@/helpers/sequence-index-ws.helper';
import { useLiveSequenceIndexStore } from '@/state/stores/use-live-sequence-index-store';
import { applyLiveContextRunningFromWs } from '@/helpers/apply-live-context-running-from-ws.helper';

// Create a separate socket instance for the /player-gateway namespace
const createPlayerSocket = (): Socket => {
  const URL = `${import.meta.env.VITE_WS_URL}/player-frontend`;
  return io(URL, { autoConnect: false });
};

// Global socket instance to be shared across components
let globalPlayerSocket: Socket | null = null;
let playerSocketInstanceSeq = 0;

const getNextInstanceId = () => {
  playerSocketInstanceSeq += 1;
  return playerSocketInstanceSeq;
};

export interface PlayerSubscriptionHandler {
  onPlayersInvalidated: (entityId: string, entityType: PlayerSocketEntity) => void;
  onLiveContextInvalidated?: (entityId: string, entityType: PlayerSocketEntity, meta?: { isRunning?: boolean }) => void;
  /** Perform-action policy snapshot (seq-guarded merge in global store). */
  onLiveContextPerformControl?: (data: LiveContextPerformControlWsPayloadV1) => void;
  /** Authoritative image-sequence / PDF frame index from server auto-loop or slide control. */
  onSequenceIndexChange?: (data: SequenceIndexChangePayload) => void;
  /** Media-agent player teardown completed (VM deleted, player removed). */
  onPlayerTeardownComplete?: (data: {
    entityId: string;
    entityType: PlayerSocketEntity;
    playerId: string;
    playerName?: string;
  }) => void;
  onLiveLanguageStatus?: (data: { entityId: string; entityType: PlayerSocketEntity }) => void;
}

type EntityKey = string;

const buildEntityKey = (entityId: string, entityType: PlayerSocketEntity) => `${entityType}::${entityId}`;

const parseEntityKey = (key: EntityKey): { id: string; type: PlayerSocketEntity } => {
  const [type, ...rest] = key.split('::');
  return { type: type as PlayerSocketEntity, id: rest.join('::') };
};

class PlayerSubscriptionManager {
  private subscriptions = new Map<EntityKey, Set<PlayerSubscriptionHandler>>();
  private joinedKeys = new Set<EntityKey>();
  private handlersSetup = false;
  private lastJoinSocketId?: string;
  private joinInFlightKeys = new Set<EntityKey>();
  private lastToken?: string | null;
  private joinListeners = new Set<(key: EntityKey, joined: boolean) => void>();
  private isConnecting = false;
  private playersInvalidatedTimers = new Map<EntityKey, ReturnType<typeof setTimeout>>();

  getSocket(): Socket {
    if (!globalPlayerSocket) {
      globalPlayerSocket = createPlayerSocket();
    }
    return globalPlayerSocket;
  }

  addJoinListener(listener: (key: EntityKey, joined: boolean) => void) {
    this.joinListeners.add(listener);
    return () => this.joinListeners.delete(listener);
  }

  isJoined(key: EntityKey) {
    return this.joinedKeys.has(key);
  }

  setAuthToken(token?: string | null) {
    if (!token || token === this.lastToken) return;
    const socket = this.getSocket();
    socket.io.opts.extraHeaders = {
      Authorization: `Bearer ${token}`,
    };
    this.lastToken = token;
  }

  connect() {
    const socket = this.getSocket();
    if (socket.connected || this.isConnecting) return;
    this.isConnecting = true;
    if (!socket.connected) {
      socket.connect();
    }
  }

  subscribe(key: EntityKey, handler: PlayerSubscriptionHandler) {
    if (!this.subscriptions.has(key)) {
      this.subscriptions.set(key, new Set());
    }
    this.subscriptions.get(key)!.add(handler);
    this.ensureHandlers();
    this.connect();
    this.joinRoom(key);
  }

  unsubscribe(key: EntityKey, handler: PlayerSubscriptionHandler) {
    const handlers = this.subscriptions.get(key);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.subscriptions.delete(key);
      }
    }

    if (!this.subscriptions.has(key)) {
      this.leaveRoom(key);
    }
  }

  private notifyJoinChange(key: EntityKey, joined: boolean) {
    this.joinListeners.forEach((listener) => listener(key, joined));
  }

  private ensureHandlers() {
    if (this.handlersSetup) return;
    this.handlersSetup = true;

    const socket = this.getSocket();

    socket.on('connect', () => {
      this.isConnecting = false;
      this.lastJoinSocketId = socket.id;
      this.joinedKeys.clear();
      for (const key of this.subscriptions.keys()) {
        this.joinRoom(key);
      }
    });

    socket.on('disconnect', () => {
      this.lastJoinSocketId = undefined;
      this.joinInFlightKeys.clear();
      this.joinedKeys.clear();
      this.isConnecting = false;
      useLiveSequenceIndexStore.getState().invalidatePlayoutTiming();
    });

    socket.on('players-invalidated', (data: { entityId: string; entityType: string }) => {
      const key = buildEntityKey(data.entityId, data.entityType as PlayerSocketEntity);
      const handlers = this.subscriptions.get(key);
      if (!handlers || handlers.size === 0) return;
      // Many Flow Control / perform views subscribe; coalesce so N handlers ≠ N invalidate storms.
      const entityId = data.entityId;
      const entityType = data.entityType as PlayerSocketEntity;
      const existing = this.playersInvalidatedTimers.get(key);
      if (existing) {
        clearTimeout(existing);
      }
      this.playersInvalidatedTimers.set(
        key,
        setTimeout(() => {
          this.playersInvalidatedTimers.delete(key);
          const latest = this.subscriptions.get(key);
          latest?.forEach((handler) => handler.onPlayersInvalidated(entityId, entityType));
        }, 50),
      );
    });

    socket.on('live-context-invalidated', (data: { entityId: string; entityType: string; isRunning?: boolean }) => {
      // Patch perform mode immediately for all connected tabs (do not wait on GET_FLOW).
      if (typeof data.isRunning === 'boolean') {
        applyLiveContextRunningFromWs(data.entityId, data.isRunning, data.entityType === 'Event' ? 'Event' : 'Flow');
      }
      const key = buildEntityKey(data.entityId, data.entityType as PlayerSocketEntity);
      const handlers = this.subscriptions.get(key);
      if (!handlers) return;
      const meta = typeof data.isRunning === 'boolean' ? { isRunning: data.isRunning } : undefined;
      handlers.forEach((handler) =>
        handler.onLiveContextInvalidated?.(data.entityId, data.entityType as PlayerSocketEntity, meta),
      );
    });

    socket.on('sequence-index-change', (data: SequenceIndexChangePayload) => {
      const key = buildEntityKey(data.entityId, data.entityType as PlayerSocketEntity);
      const handlers = this.subscriptions.get(key);
      if (!handlers) return;
      handlers.forEach((handler) => handler.onSequenceIndexChange?.(data));
    });

    socket.on('live-context-perform-control', (data: LiveContextPerformControlWsPayloadV1) => {
      const roomKey = entityRoomKeyFromEntityRef(data.entityRef);
      if (!roomKey || !this.subscriptions.has(roomKey)) return;
      applyPerformControlWsPayload(roomKey, data);
      const handlers = this.subscriptions.get(roomKey);
      handlers?.forEach((handler) => handler.onLiveContextPerformControl?.(data));
    });

    socket.on('live-context-play-state', (data: LiveContextPlayStateWsPayloadV1) => {
      const roomKey = entityRoomKeyFromEntityRef(data.entityRef);
      if (!roomKey || !this.subscriptions.has(roomKey)) return;
      if (!applyLivePlayStateWsPayload(roomKey, data)) return;
      if (typeof data.emittedAt === 'number' && data.emittedAt > 0) {
        useLiveSequenceIndexStore.getState().setServerClock(data.emittedAt);
      }
    });

    socket.on('live-language-status', (data: { entityId: string; entityType: string }) => {
      const key = buildEntityKey(data.entityId, data.entityType as PlayerSocketEntity);
      const handlers = this.subscriptions.get(key);
      if (!handlers) return;
      handlers.forEach((handler) =>
        handler.onLiveLanguageStatus?.({ ...data, entityType: data.entityType as PlayerSocketEntity }),
      );
    });

    socket.on(
      'player-teardown-complete',
      (data: { entityId: string; entityType: string; playerId: string; playerName?: string }) => {
        const key = buildEntityKey(data.entityId, data.entityType as PlayerSocketEntity);
        const handlers = this.subscriptions.get(key);
        if (!handlers) return;
        handlers.forEach((handler) =>
          handler.onPlayerTeardownComplete?.({ ...data, entityType: data.entityType as PlayerSocketEntity }),
        );
      },
    );
  }

  private joinRoom(key: EntityKey) {
    const socket = this.getSocket();
    // Not connected yet — connect handler will join all subscriptions.
    if (!socket.connected) {
      this.connect();
      return;
    }

    if (this.joinInFlightKeys.has(key)) {
      return;
    }

    if (this.joinedKeys.has(key) && this.lastJoinSocketId === socket.id) {
      return;
    }

    const entity = parseEntityKey(key);
    this.joinInFlightKeys.add(key);
    let settled = false;
    const clearInFlight = () => {
      if (settled) return;
      settled = true;
      this.joinInFlightKeys.delete(key);
    };
    // Avoid stuck in-flight when ack never returns (forbidden / dropped).
    const timeoutId = window.setTimeout(() => {
      clearInFlight();
    }, 8_000);
    socket.emit(
      'join-room',
      { entityType: entity.type, entityId: entity.id },
      (response: { event: string; data: { roomId: string } }) => {
        window.clearTimeout(timeoutId);
        clearInFlight();
        if (response?.event === 'room-joined') {
          this.joinedKeys.add(key);
          this.lastJoinSocketId = socket.id;
          this.notifyJoinChange(key, true);
        }
      },
    );
  }

  private leaveRoom(key: EntityKey) {
    if (!this.joinedKeys.has(key)) return;
    const socket = this.getSocket();
    if (!socket.connected) {
      this.joinedKeys.delete(key);
      this.notifyJoinChange(key, false);
      return;
    }

    const entity = parseEntityKey(key);
    socket.emit('leave-room', { entityType: entity.type, entityId: entity.id }, () => {
      this.joinedKeys.delete(key);
      this.notifyJoinChange(key, false);
    });
  }
}

const playerSubscriptionManager = new PlayerSubscriptionManager();

export const usePlayerSocket = (
  entityId: string,
  entityType: PlayerSocketEntity,
  handler: PlayerSubscriptionHandler,
  enabled = true,
) => {
  const isEnabled = enabled ?? true;
  const handlerRef = useRef(handler);
  const instanceIdRef = useRef<number | null>(null);
  const { getToken } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [hasJoinedRoom, setHasJoinedRoom] = useState(false);

  if (instanceIdRef.current === null) {
    instanceIdRef.current = getNextInstanceId();
  }

  useEffect(() => {
    handlerRef.current = handler;
    // TODO: Add logic when isEnabled is true
    // if (isEnabled) {
    // }
  }, [handler, entityType, entityId, isEnabled]);

  useEffect(() => {
    if (!isEnabled) return;
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (cancelled) return;
      playerSubscriptionManager.setAuthToken(token);
      playerSubscriptionManager.connect();
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, isEnabled]);

  useEffect(() => {
    if (!isEnabled) {
      queueMicrotask(() => setIsConnected(false));
      return;
    }
    const socket = playerSubscriptionManager.getSocket();
    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    queueMicrotask(() => setIsConnected(socket.connected));

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, [isEnabled]);

  useEffect(() => {
    if (!isEnabled) {
      queueMicrotask(() => setHasJoinedRoom(false));
      return;
    }
    const key = buildEntityKey(entityId, entityType);
    const stableHandler: PlayerSubscriptionHandler = {
      onPlayersInvalidated: (id, type) => handlerRef.current.onPlayersInvalidated(id, type),
      onLiveContextInvalidated: (id, type, meta) => handlerRef.current.onLiveContextInvalidated?.(id, type, meta),
      onLiveContextPerformControl: (data) => handlerRef.current.onLiveContextPerformControl?.(data),
      onSequenceIndexChange: (data) => handlerRef.current.onSequenceIndexChange?.(data),
      onPlayerTeardownComplete: (data) => handlerRef.current.onPlayerTeardownComplete?.(data),
      onLiveLanguageStatus: (data) => handlerRef.current.onLiveLanguageStatus?.(data),
    };

    playerSubscriptionManager.subscribe(key, stableHandler);

    return () => {
      playerSubscriptionManager.unsubscribe(key, stableHandler);
    };
  }, [entityId, entityType, isEnabled]);

  useEffect(() => {
    if (!isEnabled) {
      queueMicrotask(() => setHasJoinedRoom(false));
      return;
    }
    const key = buildEntityKey(entityId, entityType);
    queueMicrotask(() => setHasJoinedRoom(playerSubscriptionManager.isJoined(key)));
    const unsubscribe = playerSubscriptionManager.addJoinListener((changedKey, joined) => {
      if (changedKey === key) {
        setHasJoinedRoom(joined);
      }
    });
    return () => {
      unsubscribe();
    };
  }, [entityId, entityType, isEnabled]);

  return { socket: globalPlayerSocket, isConnected, hasJoinedRoom };
};
